import React, { useMemo, useState } from 'react';
import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import { sendFeedback } from '../../../store/actions/FeedbackAction';
import BaseForm, { BaseFormSectionConfig } from '../../shared/FormComponents/BaseForm';
import { FormSchema, FormField } from '../../../utils/validation/form-schema';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, feedbackTourSteps } from '../../shared/FeatureTour';

const MAX_WORD_LIMIT = 500;

const countWords = (text: string): number => (text.trim() ? text.trim().split(/\s+/).length : 0);

const Feedback: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, AnyAction>>();
  const { t } = useTranslation();

  const [wordCount, setWordCount] = useState(0);
  // BaseForm owns the field state, and its initialValues sync deliberately
  // never overwrites a field the user has typed in — so a successful send
  // clears the form by remounting it rather than by pushing empty values down.
  const [formKey, setFormKey] = useState(0);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'feedback',
    steps: feedbackTourSteps,
    autoStartDelay: 600,
  });

  // The word limit is a field rule, so it lives in the schema: BaseForm then
  // disables the submit button and renders the message in the field's own
  // error row, instead of a hand-rolled counter/warning pair under the form.
  const schema = useMemo(
    () =>
      new FormSchema('FeedbackForm', [
        new FormField('feedback', 'body', true, t('Feedback'), {
          customValidator: (value: any) =>
            countWords(String(value ?? '')) > MAX_WORD_LIMIT
              ? { isValid: false, error: t('You have exceeded the 500-word limit') }
              : { isValid: true, sanitized: value },
        }),
      ]),
    [t]
  );

  const sections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        title: t('Share Your Feedback'),
        description: t(
          'We appreciate your insights on usability and design, features and functionality, performance and reliability, security and privacy, legal and compliance aspects, support resources, or any other area to help us improve the platform.'
        ),
        dataTourId: 'feedback-form',
        fields: [
          {
            name: 'feedback',
            type: 'textarea',
            label: t('Your Feedback'),
            placeholder: t('Enter your feedback here...'),
            required: true,
            rows: 10,
            gridSize: 'full',
            helpText: t('{{count}} / {{limit}} words', { count: wordCount, limit: MAX_WORD_LIMIT }),
          },
        ],
      },
    ],
    [t, wordCount]
  );

  const initialValues = useMemo(() => ({ feedback: '' }), []);

  const handleSubmit = async (formValues: Record<string, any>) => {
    await dispatch(sendFeedback({ FeedbackContent: String(formValues.feedback ?? '') }));
    setWordCount(0);
    setFormKey((key) => key + 1);
  };

  return (
    <>
      <BaseForm
        key={formKey}
        title={t('Share Your Feedback')}
        sections={sections}
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onValuesChange={(values) => setWordCount(countWords(String(values.feedback ?? '')))}
        submitLabel={t('Send')}
        hideHeader
        maxWidth="max-w-3xl"
      />
      <FeatureTour
        steps={feedbackTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </>
  );
};

export default Feedback;
